/**
 * Module 8: The Courage to Be Heard
 *
 * In which the first cup is yours, and Theron takes something back.
 * Primary character: all three | Supporting: Daria
 *
 * REWORKED 2026-10-04 to the six rules of docs/CONTENT_REVIEW_2026-10-04.md
 * (Evening 1 is the worked sample). This is the last evening and the mirror
 * of the first, so read module01.js beside it before changing a line.
 *
 *   What happens: the morning after the gathering. Last night is told by the
 *     people it happened to. Then Xenia hands the reader the jug: the first
 *     cup, and the few words over it, are theirs.
 *   Who is wrong, and at last not: Theron. Since Evening 1 he has held that a
 *     man can simply decide not to be afraid. He takes it back once ("I took
 *     it myself last night and it wore off as I stood up") and moves on.
 *     Nobody remarks on it. Xenia's only answer is to give him the next
 *     first cup, now that he wants it least.
 *   The one idea, shown once: nobody got steady first. No guide says so.
 *   The one useful thing: name the next real room you will speak in and
 *     prepare for that one. Theron does it badly ("I may be at sea") and then
 *     better; Xenia hands the question to the reader (Exercise 1).
 *
 * Gone on purpose, do not put back: the four "I taught you..." recap speeches,
 * the five boxes (Integration, Ongoing Practice, Two Journeys, Comparison
 * Trap, Threshold Moment), "not a destination, a practice", the three
 * farewell morals, and "Your voice is your own now. Go and use it." The
 * threshold is now the moment the jug touches the cup; it is named only in
 * the Complete Speaking Prep link, because the tool's last check uses the word.
 *
 * CONTINUITY this evening must keep:
 *   - Evening 1, mirrored line for line: "Theron has his cup out already";
 *     "In this house the first cup is poured with a few words for the
 *     evening, and tonight the words are Philemon's"; "She gives it to
 *     whoever wants it least. I have never once been asked"; the jug's lip
 *     rattling on the cup; wine on the stone; "Decide not to be afraid";
 *     "Theron finds something to look at in his cup"; Philemon's twenty
 *     heartbeats; and the course's first sentence, "The gate is unlatched
 *     when you arrive, left that way for you, you suspect", which is this
 *     evening's last.
 *   - Fixed lines (the spine): Daria's voice shook for ten heartbeats and
 *     then she found the room; her grandmother's "About time you said
 *     something worth interrupting my evening for."; Philemon's "The
 *     trembling came too. It simply didn't get to decide anything."
 *   - The three `quote` blocks of the Note card, word for word.
 *   - Evening 7: the earlier Carthage story (twenty-six, the grain merchant).
 *     There Daria says "You were frightened." and Theron answers "I was badly
 *     prepared."; here she says it again and he blames the table, and only
 *     at the jug does he give way. Also from Evening 7: the three words on
 *     the back of her hand, and her thumb clearing a space for them.
 *   - Evening 4: she wrote on her other hand too. Scene setter: old
 *     Aristocles, who sleeps through everything.
 *   - The reader's seat at the gathering is left vague on purpose ("the place
 *     you had last night", "from the benches"): Evening 3 has Xenia promise
 *     "Sitting where you are sitting now, if that is all you want."
 *
 * There are no studies in this evening and none may be added, so it has no
 * research box and no notes block.
 */

export const MODULE_08_CONTENT = [
  // --- The morning after ---
  {
    type: 'narration',
    text: 'It is the morning after, and the courtyard has not been put back. The lamps have burnt dry. A garland of bay hangs over the gate by one end, and the low table is somewhere under the cups of a hundred guests. You take the place you had last night. Philemon and Daria are carrying a borrowed bench out between them. Xenia holds the wine jug. Theron has his cup out already.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Before you say whose it is. I spoke last night. I have carried four benches this morning. And I have still never once been asked.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Two benches. The cup can wait, Theron. Our guest saw last night from the benches and ought to hear it from the front. Daria?',
  },

  // --- Last night ---
  {
    type: 'heading',
    text: 'Last night',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'I shook. The whole beginning. I could hear it in my own voice, and I thought, that is all anyone will take home: a girl shaking at them about a harbour levy.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Your voice shook for ten heartbeats. I counted. Then you looked at the back of your hand, and then you found the room.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'Ten? It was half the speech.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Ten. I have had practice at counting. Mine was twenty, and I sat down. Ask our guest how long it looked.',
  },
  {
    type: 'narration',
    text: 'They both turn to you. You go back over it as you saw it from the benches, looking for the shaking. What you find is old Aristocles, who sleeps through everything, awake and wiping his eyes.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'The grandmother, though. You left with her on your arm. She had just been made the ending of a speech. What did she say?',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'She said, "About time you said something worth interrupting my evening for." Then she asked who was walking her home.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I have bargained with Carthaginians who gave away more.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Since you mention Carthaginians: Theron told the Carthage story last night. Twice.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Someone had to outlast the poet. And it improves with repetition.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'The second time it was the earlier one.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'The grain merchant? In front of everyone?',
  },
  {
    type: 'narration',
    text: 'Theron finds something to look at in his cup. It is empty, so this does not take long.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'You held the edge of the table while you told it. I know that grip. It is mine.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'You were frightened.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'The table was unsteady.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And you, Philemon. A full courtyard, Eudoros, and no voice to borrow. Was your table unsteady?',
  },
  {
    type: 'narration',
    text: 'Philemon sets down his end of the bench.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'The trembling came too. It simply didn\'t get to decide anything.',
  },

  // --- The first cup ---
  {
    type: 'heading',
    text: 'The first cup',
  },
  {
    type: 'narration',
    text: 'Xenia comes across the courtyard with the jug, past Theron and his cup, and stops in front of you.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'In this house the first cup is poured with a few words for the day, and this morning the words are yours.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Eight evenings, and the guest gets it. She gives it to whoever wants it least.',
  },
  {
    type: 'narration',
    text: 'You stand, because Philemon stood. The jug is heavier than it looked in her hands. Its lip finds the cup and rattles there, and you know that sound. You heard it on the first evening.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Decide nothing. Pour.',
  },
  {
    type: 'narration',
    text: 'Philemon turns right round to look at him.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'On the first evening you had a cure for this. Decide not to be afraid. You would not even charge for it.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'And it was dear at the price. I took it myself last night and it wore off as I stood up. Pour.',
  },
  {
    type: 'narration',
    text: 'You pour. Most of it reaches the cup. Nobody is watching your hands. They are watching the cup.',
  },
  {
    type: 'yourTurn',
    id: 'm8-over-the-cup',
    character: 'xenia',
    speaker: 'Xenia',
    prompt: 'And the few words. They need not be fine ones. What kind have you got?',
    options: [
      {
        label: 'A thank-you, and nothing else.',
        reply: {
          character: 'xenia',
          speaker: 'Xenia',
          text: 'To whom, and for what? "Thank you all" is said to nobody. Pick one of us, one thing, and say it to that face. Then do the same in the next room: one face first, and your first sentence to them.',
        },
      },
      {
        label: 'The truth. That my hands are not steady.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'Then say it once, plainly, and do not apologise. "My hands are shaking. Here is what I came to say." I have opened that way more often than I admit. It is the second sentence they keep.',
        },
      },
      {
        label: 'Something to make them laugh.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'Good. Aim it at yourself, never at the room, and keep it shorter than the pouring. Then say the true thing while they are still smiling. A laugh buys you one sentence of goodwill. Spend it at once.',
        },
      },
      {
        label: 'Nothing yet. I would want to prepare them first.',
        reply: {
          character: 'daria',
          speaker: 'Daria',
          text: 'I had seventeen points and most of a month. You have a cup. Pick three words, say one sentence on each, and stop. There is room on the back of your hand. I have used both of mine.',
        },
      },
    ],
  },
  {
    type: 'narration',
    text: 'You say them. There is wine on your fingers and on the stone, and the morning has begun. Xenia takes back the jug and turns to Theron.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'At the next gathering, the first cup is yours.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Mine. Good. At last. When is that? I may be at sea.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'The new moon. This courtyard. Who will be in it?',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'The same hundred. And that poet.',
  },
  {
    type: 'narration',
    text: 'He has gone a colour you have not seen on him before. Daria, who knows that colour, wets her thumb and starts to clear three smudged words off the back of her hand.',
  },
  {
    type: 'dialogue',
    character: 'daria',
    speaker: 'Daria',
    text: 'I need my hand back. The men who set the levy weren\'t here last night.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And you. Not this courtyard. Which room is next, out there, and when?',
  },
  {
    type: 'exercise',
    number: 1,
    title: 'The next room',
    instructions: 'Answer Xenia. It only works with a real room, so choose one that is coming: a meeting, a toast, a call you have been putting off.',
    prompts: [
      'Which room is it? Give the place, the day and what you are there to say.',
      'Who will be in it? Name one face you can find first.',
      'What would you settle for? Leave triumph out of it: the one thing that has to be said before you sit down.',
      'If nothing is coming, make something small come: a question at a meeting, a few words at a meal.',
    ],
  },

  // --- At the gate ---
  {
    type: 'heading',
    text: 'At the gate',
  },
  {
    type: 'narration',
    text: 'Nobody makes a speech when you go. Philemon lifts a hand from his end of the bench. Theron does not see you leave: he is saying a few words to the empty benches, stopping, and starting again. At the gate Xenia presses a folded scrap of papyrus into your hand. Three lines, in three different hands.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Read it out there. In here, Theron will stop and explain his.',
  },
  {
    type: 'quote',
    text: 'The fear is proof you care. I do not shake over things I do not care about, and neither do you.',
    attribution: 'Philemon',
  },
  {
    type: 'quote',
    text: 'You belong in any room you have the courage to enter. Stand like it.',
    attribution: 'Theron',
  },
  {
    type: 'quote',
    text: 'The world needs your voice. Not perfect, just yours. Give it.',
    attribution: 'Xenia',
  },
  {
    type: 'narration',
    text: 'You read it in the street, which is already loud. Your thumb has left a print of wine beneath the third line. When you look back, the gate is unlatched, left that way for you, you suspect.',
  },

  {
    type: 'divider',
  },

  // --- To take away ---
  {
    type: 'heading',
    text: 'For your next room',
  },
  {
    type: 'exercise',
    number: 2,
    title: 'Walk that room through',
    instructions: 'Take the room you named and walk it through once before you are in it. A line for each step is enough.',
    prompts: [
      'The night before: run the Complete Speaking Prep for this room, and write your three anchor words where you will see them.',
      'At the door: feet, shoulders, jaw, three breaths. Expect the shaking. Daria\'s lasted ten heartbeats.',
      'If it wobbles while you are speaking: stop, feel your feet, breathe out slowly, look at one person, go on. The Quick Reset is that sequence.',
      'The morning after: ask one person who was there what they noticed, and set it beside how it felt from inside. The Courtyard Journal in the toolkit has five questions for the rest.',
      'Then name the room after that one.',
    ],
  },
  {
    type: 'toolLink',
    toolId: 'complete-speaking-prep',
    title: 'Complete Speaking Prep',
    text: 'For the night before. Eight checks to tick off: one from each of the seven evenings before this one, and a last one for the breath before you begin, which the tool calls the Threshold. Deep mode shows the question behind each check and gives you room for a note.',
  },
  {
    type: 'toolLink',
    toolId: 'quick-reset',
    title: 'Quick Reset',
    text: 'For the moment itself, just before you speak or part-way through. Five short steps that move on by themselves in a little over two minutes: stop, ground, breathe, refocus, continue. You can skip straight to the breathing.',
  },

  // --- Module Summary ---
  {
    type: 'summary',
    characterId: 'xenia',
    points: [
      'Daria shook, Philemon trembled, Theron held the table and you spilt the wine. Everyone spoke. Nobody got steady first.',
      'Name your next real room: the place, the day, who will be there. Prepare for that one room, not for speaking in general.',
      'Complete Speaking Prep the night before, Quick Reset in the moment, and the morning after, ask someone who was there what they noticed.',
    ],
  },
];

export const MODULE_08_EXERCISES = [
  {
    number: 1,
    title: 'The next room',
    description: 'Name the next real room you will speak in: the place, the day and who will be there.',
    xpReward: 30,
  },
  {
    number: 2,
    title: 'Walk that room through',
    description: 'Plan the night before, the moment itself and the morning after for the room you named.',
    xpReward: 20,
  },
];

export const MODULE_08_SUMMARY = {
  title: 'The Courage to Be Heard',
  keyInsight: 'The trembling comes too. Name the next room anyway.',
  characterQuote: {
    character: 'philemon',
    text: 'The trembling came too. It simply didn\'t get to decide anything.',
  },
  takeaways: [
    'Nobody in the courtyard got steady first.',
    'Name your next real room and prepare for that one.',
    'Complete Speaking Prep the night before, Quick Reset in the moment.',
  ],
};
